'use client';

import React, { useCallback, useEffect, useState } from 'react';
import AdminLayout from '../../components/AdminLayout';
import axiosInstance from '../../api/axios';
import './Dashboard.css';
import {
  BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell,
} from 'recharts';
import { Link } from 'react-router-dom';
import { requestWithFallback } from '../../services/adminCrudApi';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalPasien: 0,
    totalDokter: 0,
    totalPendaftaran: 0,
    totalLayanan: 0,
    pendaftaranHariIni: 0,
    pasienBaru: 0,
    dokterAktif: 0,
  });

  const [serviceData, setServiceData] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState([]);
  const [chartLoading, setChartLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('month');
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [kunjunganChart, setKunjunganChart] = useState([]);

  /* Deteksi Screen Mobile */
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const fetchAnalytics = useCallback(async () => {
    try {
      const res = await requestWithFallback([
        { method: 'get', url: '/admin/analytics' },
      ]);
      setKunjunganChart(res.data.chart);
    } catch (err) {
      console.error('Gagal ambil analytics', err);
      setKunjunganChart([]);
    }
  }, []);

  const fetchDashboardData = useCallback(async () => {
    try {
      const statsRes = await requestWithFallback([
        { method: 'get', url: '/admin/dashboard' },
        { method: 'get', url: '/dashboard' },
        { method: 'get', url: '/dashboard/stats' },
      ]);
      const data = statsRes.data;

      setStats({
        totalPasien: data.totalPasien || data.total_pasien || 0,
        totalDokter: data.totalDokter || data.total_dokter || 0,
        totalPendaftaran: data.totalPendaftaran || data.total_pendaftaran || 0,
        totalLayanan: data.totalLayanan || data.total_layanan || data.statistikDokter?.spesialisasi?.length || 0,
        pendaftaranHariIni: data.pendaftaranHariIni || data.pendaftaran_hari_ini || 0,
        pasienBaru: data.pasienBaru || data.pasien_baru || data.statistikPasien?.pasienBaruHariIni || 0,
        dokterAktif: data.dokterAktif || data.dokter_aktif || data.totalDokter || 0,
      });

      if (data.statistikDokter?.spesialisasi?.length > 0) {
        setServiceData(
          data.statistikDokter.spesialisasi.map((s) => ({
            name: s.spesialisasi || s.nama || 'Umum',
            value: parseInt(s.total || s.jumlah || 0, 10),
          }))
        );
      } else if (data.layanan || data.services) {
        const services = data.layanan || data.services || [];
        setServiceData(
          services.map((s, i) => ({
            name: s.nama || s.name || `Layanan ${i + 1}`,
            value: parseInt(s.total || s.jumlah || 0, 10),
          }))
        );
      }

      try {
        const activitiesRes = await axiosInstance.get('/admin/aktivitas-hari-ini');
        const actData = Array.isArray(activitiesRes.data)
          ? activitiesRes.data
          : Array.isArray(activitiesRes.data?.data) ? activitiesRes.data.data : [];
        setRecentActivities(
          actData
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
            .slice(0, 3)
        );
      } catch {
        setRecentActivities([]);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setStats({ totalPasien: 0, totalDokter: 0, totalPendaftaran: 0, totalLayanan: 0, pendaftaranHariIni: 0, pasienBaru: 0, dokterAktif: 0 });
      setServiceData([]);
      setRecentActivities([]);
    } finally {
      setLoading(false);
      setLastUpdated(new Date());
    }
  }, []);

  const fetchChartData = useCallback(async (range) => {
    setChartLoading(true);
    try {
      const response = await requestWithFallback([
        { method: 'get', url: `/admin/chart-data?range=${range}` },
        { method: 'get', url: `/dashboard/chart-data?range=${range}` },
        { method: 'get', url: `/admin/dashboard/chart?range=${range}` },
      ]);
      if (response.data && Array.isArray(response.data) && response.data.length > 0) {
        setChartData(response.data);
      } else {
        setChartData([]);
      }
    } catch {
      setChartData([]);
    } finally {
      setChartLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
    fetchAnalytics();
    fetchChartData(timeRange);
  }, [fetchDashboardData, fetchAnalytics, fetchChartData]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!loading && !refreshing) {
        fetchDashboardData();
        fetchChartData(timeRange);
        fetchAnalytics();
      }
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [loading, refreshing, timeRange, fetchDashboardData, fetchChartData, fetchAnalytics]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchDashboardData();
    await fetchChartData(timeRange);
    setRefreshing(false);
    setLastUpdated(new Date());
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const diffMs = new Date() - new Date(dateString);
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'Baru saja';
    if (diffMins < 60) return `${diffMins} menit lalu`;
    if (diffHours < 24) return `${diffHours} jam lalu`;
    return `${diffDays} hari lalu`;
  };

  /* Mapper Konfigurasi Warna Badge Aktivitas */
  const badgeMap = {
    pendaftaran_baru: { label: 'Pendaftaran', bg: '#EFF6FF', text: '#1D4ED8', dot: '#3B82F6' },
    jadwal_periksa:   { label: 'Jadwal Periksa', bg: '#FEF3C7', text: '#B45309', dot: '#F59E0B' },
    rekam_medis:      { label: 'Rekam Medis', bg: '#DCFCE7', text: '#15803D', dot: '#10B981' },
  };

  return (
    <AdminLayout>
      <div style={{ padding: isMobile ? '0px' : '4px' }}>
        
        {/* ── HEADER ── */}
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'flex-start' : 'center',
          gap: '16px',
          marginBottom: '24px',
          backgroundColor: '#FFFFFF',
          padding: isMobile ? '16px' : '20px 24px',
          borderRadius: '12px',
          border: '1px solid #E5E7EB',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        }}>
          <div>
            <h1 style={{ fontSize: isMobile ? '20px' : '24px', fontWeight: '800', color: '#111827', margin: 0 }}>
              Dashboard Admin
            </h1>
            <p style={{ color: '#6B7280', margin: '4px 0 0', fontSize: '13px' }}>
              Selamat datang di dashboard administratif Klinik Medis
            </p>
            <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '6px' }}>
              {new Date().toLocaleDateString('id-ID', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} &bull; Terakhir diperbarui: {lastUpdated.toLocaleTimeString('id-ID')}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '8px', width: isMobile ? '100%' : 'auto' }}>
            <Link
              to="/"
              style={{
                flex: isMobile ? 1 : 'none',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                textDecoration: 'none',
                color: '#374151',
                padding: '8px 14px',
                borderRadius: '8px',
                border: '1px solid #D1D5DB',
                backgroundColor: '#FFFFFF',
                fontSize: '13px',
                fontWeight: 600,
              }}
            >
              Ke Beranda
            </Link>
            <button
              onClick={handleRefresh}
              disabled={refreshing || loading}
              style={{
                flex: isMobile ? 1 : 'none',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#FFFFFF',
                padding: '8px 14px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: '#2563EB',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
                opacity: refreshing || loading ? 0.7 : 1,
              }}
            >
              {refreshing ? 'Memuat...' : '↻ Refresh Data'}
            </button>
          </div>
        </div>

        {loading ? (
          <div style={{ padding: '60px 0', textAlign: 'center', color: '#6B7280', fontSize: '14px' }}>
            <div style={{ fontSize: '20px', marginBottom: '8px' }}>⏳</div>
            Memuat data dashboard…
          </div>
        ) : (
          <>
            {/* ── STAT CARDS ── */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: isMobile ? '12px' : '16px',
              marginBottom: '24px',
            }}>
              <div style={cardStyle}>
                <div style={labelStyle}>Total Pasien</div>
                <div style={{ fontSize: isMobile ? '22px' : '28px', fontWeight: '800', color: '#1E40AF', marginTop: '4px' }}>
                  {stats.totalPasien.toLocaleString()}
                </div>
                <div style={{ ...pillStyle, backgroundColor: '#EFF6FF', color: '#1D4ED8' }}>
                  {stats.pasienBaru > 0 ? `+${stats.pasienBaru} baru bulan ini` : 'Data terbaru'}
                </div>
              </div>

              <div style={cardStyle}>
                <div style={labelStyle}>Total Dokter</div>
                <div style={{ fontSize: isMobile ? '22px' : '28px', fontWeight: '800', color: '#166534', marginTop: '4px' }}>
                  {stats.totalDokter}
                </div>
                <div style={{ ...pillStyle, backgroundColor: '#DCFCE7', color: '#15803D' }}>
                  {stats.dokterAktif > 0 ? `${stats.dokterAktif} aktif` : 'Data terbaru'}
                </div>
              </div>

              <div style={cardStyle}>
                <div style={labelStyle}>Total Pendaftaran</div>
                <div style={{ fontSize: isMobile ? '22px' : '28px', fontWeight: '800', color: '#B45309', marginTop: '4px' }}>
                  {stats.totalPendaftaran.toLocaleString()}
                </div>
                <div style={{ ...pillStyle, backgroundColor: '#FEF3C7', color: '#B45309' }}>
                  {stats.pendaftaranHariIni > 0 ? `Hari ini: ${stats.pendaftaranHariIni}` : 'Data terbaru'}
                </div>
              </div>

              <div style={cardStyle}>
                <div style={labelStyle}>Total Layanan</div>
                <div style={{ fontSize: isMobile ? '22px' : '28px', fontWeight: '800', color: '#475569', marginTop: '4px' }}>
                  {stats.totalLayanan}
                </div>
                <div style={{ ...pillStyle, backgroundColor: '#F1F5F9', color: '#475569' }}>
                  {serviceData.length > 0 ? `${serviceData.length} kategori` : 'Data terbaru'}
                </div>
              </div>
            </div>

            {/* ── AKTIVITAS TERBARU ── */}
            <div style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '12px',
              border: '1px solid #E5E7EB',
              boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
              overflow: 'hidden',
            }}>
              <div style={{
                display: 'flex',
                justify: 'space-between',
                alignItems: 'center',
                padding: '16px 24px',
                borderBottom: '1px solid #E5E7EB',
                backgroundColor: '#FAFAFA',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '16px' }}>⚡</span>
                  <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#111827', margin: 0 }}>
                    Aktivitas Terbaru
                  </h3>
                </div>
                <Link
                  to="/admin/aktivitas"
                  style={{
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#2563EB',
                    textDecoration: 'none',
                    marginLeft: '24px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Lihat semua →
                </Link>
              </div>

              <div style={{ padding: '8px 24px' }}>
                {recentActivities.length > 0 ? (
                  recentActivities.map((activity, index) => {
                    const badge = badgeMap[activity.type] ?? {
                      label: 'Aktivitas',
                      bg: '#F3F4F6',
                      text: '#4B5563',
                      dot: '#9CA3AF',
                    };

                    return (
                      <div
                        key={activity.id || index}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '14px',
                          padding: '14px 0',
                          borderBottom: index !== recentActivities.length - 1 ? '1px solid #F3F4F6' : 'none',
                        }}
                      >
                        {/* Dot Indikator Waktu */}
                        <div style={{
                          width: '10px',
                          height: '10px',
                          borderRadius: '50%',
                          backgroundColor: badge.dot,
                          marginTop: '5px',
                          flexShrink: 0,
                          boxShadow: `0 0 0 3px ${badge.bg}`,
                        }} />

                        {/* Konten Aktivitas */}
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '4px' }}>
                            <span style={{
                              fontSize: '11px',
                              fontWeight: '700',
                              padding: '3px 8px',
                              borderRadius: '6px',
                              backgroundColor: badge.bg,
                              color: badge.text,
                            }}>
                              {badge.label}
                            </span>
                            <span style={{ fontSize: '11px', color: '#9CA3AF', fontWeight: '500' }}>
                              {formatDate(activity.timestamp || activity.created_at)}
                            </span>
                          </div>
                          <p style={{ margin: 0, fontSize: '13px', color: '#374151', lineHeight: '1.5', fontWeight: '500' }}>
                            {activity.description ?? '-'}
                          </p>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div style={{ padding: '32px 0', textAlign: 'center' }}>
                    <p style={{ margin: 0, fontSize: '13px', color: '#6B7280', fontWeight: '500' }}>
                      Belum ada aktivitas baru hari ini.
                    </p>
                    <span style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '4px', display: 'block' }}>
                      Aktivitas pendaftaran & kunjungan pasien akan tampil di sini secara real-time.
                    </span>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

/* ── STYLES CONSTANTS ── */
const cardStyle = {
  backgroundColor: '#FFFFFF',
  borderRadius: '12px',
  border: '1px solid #E5E7EB',
  padding: '16px 20px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
};

const labelStyle = {
  fontSize: '12px',
  fontWeight: '600',
  color: '#6B7280',
};

const pillStyle = {
  display: 'inline-block',
  marginTop: '8px',
  padding: '2px 8px',
  borderRadius: '12px',
  fontSize: '11px',
  fontWeight: '600',
};

export default AdminDashboard;